<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 15,
  },
  currentPageInfo: {
    type: String,
    default: '',
  },
  showPerPageSelector: {
    type: Boolean,
    default: false,
  },
  perPageOptions: {
    type: Array,
    default: () => [15, 25, 50, 100],
  },
});
const emit = defineEmits(['update:currentPage', 'update:itemsPerPage']);
const { t } = useI18n();
const { formatCompactNumber, formatFullNumber } = useNumberFormatter();

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);
const startItem = computed(
  () => (props.currentPage - 1) * props.itemsPerPage + 1
);
const endItem = computed(() =>
  Math.min(startItem.value + props.itemsPerPage - 1, props.totalItems)
);
const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === totalPages.value);
const changePage = newPage => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    emit('update:currentPage', newPage);
  }
};

const customInput = ref('');
const showPerPagePopover = ref(false);

const selectPerPage = value => {
  customInput.value = '';
  showPerPagePopover.value = false;
  emit('update:itemsPerPage', value);
};

const closePerPagePopover = () => {
  showPerPagePopover.value = false;
  customInput.value = '';
};

const itemsPerPageLabel = computed(() =>
  t(
    'PAGINATION_FOOTER.ITEMS_PER_PAGE',
    { count: props.itemsPerPage },
    Number(props.itemsPerPage)
  )
);

const handleCustomInputKeyup = event => {
  if (event.key === 'Enter') {
    const value = parseInt(customInput.value, 10);
    if (value > 0) {
      selectPerPage(value);
    }
  }
};

const currentPageInformation = computed(() => {
  const translationKey = props.currentPageInfo || 'PAGINATION_FOOTER.SHOWING';
  return t(
    translationKey,
    {
      startItem: formatFullNumber(startItem.value),
      endItem: formatFullNumber(endItem.value),
      totalItems: formatCompactNumber(props.totalItems),
    },
    Number(props.totalItems)
  );
});

const pageInfo = computed(() => {
  return t(
    'PAGINATION_FOOTER.CURRENT_PAGE_INFO',
    {
      currentPage: '',
      totalPages: formatCompactNumber(totalPages.value),
    },
    Number(totalPages.value)
  );
});
</script>

<template>
  <div
    class="flex justify-between h-[3.375rem] w-full border-t border-n-weak mx-auto bg-n-surface-1 py-3 px-6 items-center before:absolute before:inset-x-0 before:-top-4 before:bg-gradient-to-t before:from-n-surface-1 before:from-0% before:to-transparent before:h-4 before:pointer-events-none"
  >
    <div class="flex items-center gap-3">
      <span class="min-w-0 text-body-main line-clamp-1 text-n-slate-11">
        {{ currentPageInformation }}
      </span>
      <div
        v-if="showPerPageSelector"
        v-on-click-outside="closePerPagePopover"
        class="relative"
      >
        <button
          class="px-1.5 py-0.5 rounded-md text-body-main text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors tabular-nums"
          @click="showPerPagePopover = !showPerPagePopover"
        >
          {{ itemsPerPageLabel }}
        </button>
        <div
          v-if="showPerPagePopover"
          class="absolute bottom-full mb-2 left-0 z-50"
        >
          <div
            class="bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl shadow-sm py-2 px-2 min-w-[220px]"
          >
            <div class="flex flex-wrap gap-1.5 mb-2">
              <button
                v-for="option in perPageOptions"
                :key="option"
                class="px-3 py-1 rounded-lg text-sm transition-colors"
                :class="
                  itemsPerPage === option
                    ? 'bg-n-blue-9 text-n-blue-3 font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="selectPerPage(option)"
              >
                {{ option }}
              </button>
            </div>
            <div class="flex items-center gap-2 pt-1 border-t border-n-weak">
              <input
                v-model="customInput"
                type="number"
                min="1"
                :placeholder="t('PAGINATION_FOOTER.CUSTOM_PLACEHOLDER')"
                class="w-full px-2 py-1 rounded-lg text-sm bg-n-input-background border border-n-weak text-n-slate-12 outline-none focus:border-n-blue-6 transition-colors"
                @keyup="handleCustomInputKeyup"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <Button
        icon="i-lucide-chevrons-left"
        variant="ghost"
        size="sm"
        color="slate"
        class="!w-8 !h-6"
        :disabled="isFirstPage"
        @click="changePage(1)"
      />
      <Button
        icon="i-lucide-chevron-left"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :disabled="isFirstPage"
        @click="changePage(currentPage - 1)"
      />
      <div class="inline-flex items-center gap-2 text-sm">
        <span
          class="px-3 tabular-nums py-0.5 font-420 bg-n-input-background text-body-main text-n-slate-12 rounded-md"
        >
          {{ formatFullNumber(currentPage) }}
        </span>
        <span class="truncate text-body-main text-n-slate-11">
          {{ pageInfo }}
        </span>
      </div>
      <Button
        icon="i-lucide-chevron-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :disabled="isLastPage"
        @click="changePage(currentPage + 1)"
      />
      <Button
        icon="i-lucide-chevrons-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-6"
        :disabled="isLastPage"
        @click="changePage(totalPages)"
      />
    </div>
  </div>
</template>
